// ============================================================================
// PHASE TEMPLATE CATALOG PAGE
// Browse and manage phase templates — table style
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Plus, Edit2, Trash2, Copy, Send, ChevronRight,
  Layers, MoreHorizontal, Eye
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { UserProfile } from '../services/authService';
import {
  PhaseTemplate,
  TemplateScope,
  PhaseCategory,
  TemplatePublishingStatus,
  BrowseTemplatesParams
} from '../types';
import {
  browsePhaseTemplates,
  deletePhaseTemplate,
  submitTemplateForReview
} from '../services/templateService';

interface PhaseFilterParams {
  selectedScope: TemplateScope | '';
  selectedCategory: PhaseCategory | '';
  searchTerm: string;
  showPrebuiltOnly: boolean;
}

async function fetchAllScopedPhases(user: UserProfile | null): Promise<PhaseTemplate[]> {
  const [userPhases, orgPhases, sessionPhases, prebuiltPhases] = await Promise.all([
    browsePhaseTemplates({ scope: TemplateScope.USER, ownerId: user?.userId }).catch(() => ({ templates: [] })),
    browsePhaseTemplates({ scope: TemplateScope.ORGANIZATION, ownerId: user?.userId }).catch(() => ({ templates: [] })),
    browsePhaseTemplates({ scope: TemplateScope.SESSION }).catch(() => ({ templates: [] })),
    browsePhaseTemplates({ isPrebuilt: true }).catch(() => ({ templates: [] }))
  ]);

  const combined = [
    ...userPhases.templates,
    ...orgPhases.templates,
    ...sessionPhases.templates,
    ...prebuiltPhases.templates
  ];

  const uniqueMap = new Map<string, PhaseTemplate>();
  combined.forEach(phase => {
    const key = `${phase.phaseTemplateId}-${phase.versionNumber}`;
    if (!uniqueMap.has(key)) {
      uniqueMap.set(key, phase);
    }
  });

  return Array.from(uniqueMap.values());
}

async function fetchFilteredPhases(
  filters: PhaseFilterParams,
  user: UserProfile | null
): Promise<PhaseTemplate[]> {
  const { selectedScope, selectedCategory, searchTerm, showPrebuiltOnly } = filters;

  const params: BrowseTemplatesParams = {
    scope: selectedScope || undefined,
    category: selectedCategory || undefined,
    isPrebuilt: showPrebuiltOnly || undefined,
    search: searchTerm || undefined
  };

  if ((selectedScope === TemplateScope.USER || selectedScope === TemplateScope.ORGANIZATION) && user?.userId) {
    params.ownerId = user.userId;
  }

  const result = await browsePhaseTemplates(params);
  return result.templates;
}

const CATEGORY_STYLES: Record<string, string> = {
  KICKOFF: 'bg-amber-100 text-amber-700',
  DISCOVERY: 'bg-blue-100 text-blue-700',
  IDEATION: 'bg-pink-100 text-pink-700',
  RESEARCH: 'bg-cyan-100 text-cyan-700',
  ANALYSIS: 'bg-indigo-100 text-indigo-700',
  WRAP_UP: 'bg-emerald-100 text-emerald-700',
  CUSTOM: 'bg-violet-100 text-violet-700',
};

const STATUS_STYLES: Record<string, string> = {
  DRAFT: 'bg-slate-100 text-slate-600',
  PENDING_REVIEW: 'bg-amber-100 text-amber-700',
  APPROVED: 'bg-emerald-100 text-emerald-700',
  REJECTED: 'bg-red-100 text-red-600',
};

export default function PhaseTemplateCatalog() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [phases, setPhases] = useState<PhaseTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedScope, setSelectedScope] = useState<TemplateScope | ''>('');
  const [selectedCategory, setSelectedCategory] = useState<PhaseCategory | ''>('');
  const [showPrebuiltOnly, setShowPrebuiltOnly] = useState(false);

  // Action menu
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const fetchPhases = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let allPhases: PhaseTemplate[] = [];

      if (!selectedScope && !selectedCategory && !searchTerm && !showPrebuiltOnly && user?.userId) {
        allPhases = await fetchAllScopedPhases(user);
      } else {
        allPhases = await fetchFilteredPhases(
          { selectedScope, selectedCategory, searchTerm, showPrebuiltOnly },
          user
        );
      }

      setPhases(allPhases);
    } catch (err: any) {
      setError(err.message || 'Failed to load phase templates');
    } finally {
      setLoading(false);
    }
  }, [selectedScope, selectedCategory, searchTerm, showPrebuiltOnly, user]);

  useEffect(() => {
    fetchPhases();
  }, [fetchPhases]);

  const handleSearch = () => {
    fetchPhases();
  };

  const handleDelete = async (phaseTemplateId: string, versionNumber: number) => {
    if (!window.confirm('Are you sure you want to delete this phase template? This cannot be undone.')) return;
    try {
      await deletePhaseTemplate(phaseTemplateId, versionNumber);
      fetchPhases();
    } catch (err: any) {
      alert(`Failed to delete phase template: ${err.message}`);
    }
  };

  const handleClone = (phase: PhaseTemplate) => {
    const newName = window.prompt(`Clone "${phase.name}" as:`, `${phase.name} (Copy)`);
    if (!newName) return;
    navigate('/templates/phases/create', {
      state: { cloneFrom: phase, name: newName }
    });
  };

  const handlePublish = async (phase: PhaseTemplate) => {
    const notes = window.prompt(`Submit "${phase.name}" for review. Add notes (optional):`);
    if (notes === null) return;
    try {
      await submitTemplateForReview('PHASE', phase.phaseTemplateId, phase.versionNumber, 'ORGANIZATION', notes || undefined);
      alert('Phase template submitted for review!');
      fetchPhases();
    } catch (err: any) {
      alert(`Failed to submit for review: ${err.message}`);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">

        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <button onClick={() => navigate('/templates')} className="hover:text-primary transition-colors">
            Templates
          </button>
          <ChevronRight className="w-4 h-4" />
          <span className="text-slate-700 font-medium">Phase Library</span>
        </div>

        {/* Header */}
        <header className="flex items-center justify-between border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <Layers size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">Phase Library</h1>
              <p className="text-slate-500 text-sm font-medium">Browse and manage reusable workshop phase templates</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/templates')}
              className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors"
            >
              Session Templates
            </button>
            <button
              onClick={() => navigate('/templates/phases/create')}
              className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm"
            >
              <Plus size={16} />
              Create Phase
            </button>
          </div>
        </header>

        {/* Filters + Table — one unit */}
        <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-[#e2e8f0]">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="flex-1 min-w-[300px] relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search phase templates..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-[#e2e8f0] bg-white text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all placeholder:text-slate-400 text-sm"
                />
              </div>
              <select
                value={selectedScope}
                onChange={(e) => setSelectedScope(e.target.value as TemplateScope | '')}
                className="px-3 py-2.5 rounded-xl border border-[#e2e8f0] text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none cursor-pointer"
              >
                <option value="">All Scopes</option>
                <option value={TemplateScope.SESSION}>Session</option>
                <option value={TemplateScope.USER}>User</option>
                <option value={TemplateScope.ORGANIZATION}>Organization</option>
              </select>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value as PhaseCategory | '')}
                className="px-3 py-2.5 rounded-xl border border-[#e2e8f0] text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none cursor-pointer"
              >
                <option value="">All Categories</option>
                <option value={PhaseCategory.KICKOFF}>Kickoff</option>
                <option value={PhaseCategory.DISCOVERY}>Discovery</option>
                <option value={PhaseCategory.IDEATION}>Ideation</option>
                <option value={PhaseCategory.RESEARCH}>Research</option>
                <option value={PhaseCategory.ANALYSIS}>Analysis</option>
                <option value={PhaseCategory.WRAP_UP}>Wrap Up</option>
                <option value={PhaseCategory.CUSTOM}>Custom</option>
              </select>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-navy whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={showPrebuiltOnly}
                  onChange={(e) => setShowPrebuiltOnly(e.target.checked)}
                  className="rounded border-slate-300 text-primary focus:ring-primary h-4 w-4"
                />
                Prebuilt only
              </label>
            </div>
          </div>

          {loading && (
            <div className="flex justify-center items-center py-20 text-slate-400">
              <div className="text-sm font-medium">Loading phase templates...</div>
            </div>
          )}
          {!loading && error && (
            <div className="p-6 text-center">
              <p className="text-sm text-red-700">Failed to load phase templates: {error}</p>
            </div>
          )}
          {!loading && !error && phases.length === 0 && (
            <div className="p-16 text-center">
              <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <Layers className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-slate-500 text-sm">
                {searchTerm || selectedScope || selectedCategory ? 'No phase templates found.' : 'No phase templates yet.'}
              </p>
              <p className="text-slate-400 text-xs mt-2">
                {searchTerm || selectedScope || selectedCategory ? 'Try adjusting your filters.' : 'Create your first phase template to get started.'}
              </p>
            </div>
          )}
          {!loading && !error && phases.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e2e8f0] bg-slate-50/50">
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Scope</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Duration</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Criteria</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Version</th>
                    <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-400">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e2e8f0]">
                  {phases.map((phase) => {
                    const rowKey = `${phase.phaseTemplateId}-${phase.versionNumber}`;
                    const catStyle = CATEGORY_STYLES[phase.category] || 'bg-slate-100 text-slate-600';
                    const statusStyle = STATUS_STYLES[phase.publishingStatus] || 'bg-slate-100 text-slate-600';
                    const menuOpen = openMenuId === rowKey;

                    return (
                      <tr
                        key={rowKey}
                        className="hover:bg-slate-50/50 transition-colors cursor-pointer"
                        onClick={() => navigate(`/templates/phases/${phase.phaseTemplateId}`)}
                      >
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <Layers className="w-4 h-4 text-primary" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-navy truncate max-w-[260px]">{phase.name}</p>
                              <p className="text-xs text-slate-500 truncate max-w-[260px]">{phase.description}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold uppercase ${catStyle}`}>
                            {phase.category}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-xs text-slate-500">{phase.scope}</td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold ${statusStyle}`}>
                            {phase.publishingStatus.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-xs text-slate-500">{phase.duration}</td>
                        <td className="px-4 py-4 text-xs text-slate-500">{phase.successCriteria.length}</td>
                        <td className="px-4 py-4 text-xs text-slate-400">v{phase.versionNumber}</td>
                        <td className="px-4 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="relative inline-block">
                            <button
                              onClick={() => setOpenMenuId(menuOpen ? null : rowKey)}
                              className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors text-slate-500 hover:text-slate-700"
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </button>
                            {menuOpen && (
                              <>
                                <div className="fixed inset-0 z-10" onClick={() => setOpenMenuId(null)} />
                                <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-20 w-44">
                                  <button
                                    onClick={() => { navigate(`/templates/phases/${phase.phaseTemplateId}`); setOpenMenuId(null); }}
                                    className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2.5"
                                  >
                                    <Eye className="w-4 h-4 text-slate-400" /> View
                                  </button>
                                  <button
                                    onClick={() => { navigate(`/templates/phases/${phase.phaseTemplateId}/edit`); setOpenMenuId(null); }}
                                    className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2.5"
                                  >
                                    <Edit2 className="w-4 h-4 text-slate-400" /> Edit
                                  </button>
                                  <button
                                    onClick={() => { handleClone(phase); setOpenMenuId(null); }}
                                    className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2.5"
                                  >
                                    <Copy className="w-4 h-4 text-slate-400" /> Clone
                                  </button>
                                  {phase.publishingStatus === TemplatePublishingStatus.DRAFT && phase.scope === TemplateScope.USER && (
                                    <button
                                      onClick={() => { handlePublish(phase); setOpenMenuId(null); }}
                                      className="w-full text-left px-4 py-2.5 text-sm text-primary hover:bg-slate-50 flex items-center gap-2.5"
                                    >
                                      <Send className="w-4 h-4" /> Publish
                                    </button>
                                  )}
                                  {phase.publishingStatus === TemplatePublishingStatus.DRAFT && (
                                    <>
                                      <div className="my-1 border-t border-slate-100" />
                                      <button
                                        onClick={() => { handleDelete(phase.phaseTemplateId, phase.versionNumber); setOpenMenuId(null); }}
                                        className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2.5"
                                      >
                                        <Trash2 className="w-4 h-4" /> Delete
                                      </button>
                                    </>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {!loading && !error && phases.length > 0 && (
          <p className="text-sm text-slate-400">
            Showing {phases.length} phase template{phases.length !== 1 ? 's' : ''}
          </p>
        )}

      </div>
    </div>
  );
}
